import { map } from 'lodash-es';

export default {

  /**
   * @param state
   * @param getters
   * @returns {Array}
   */
  getSeasonsWithRelations(state, getters) {
    const todayDate = getters.getTodayDate;
    return map(state.seasons.items, season => ({
      id: season.id,
      twentySupplierId: season.twentySupplierId,
      name: season.name,
      webActive: season.webActive,
      active: season.active,
      bookDaysBeforeArrival: season.bookDaysBeforeArrival,
      netOrGross: season.netOrGross,
      priceSettings: season.priceSettings,
      numPersonCategories: Object.keys(season.personCategories).length,
      numPeriodDates: Object.keys(
        season.periods.filter(
          periodDate => (
            periodDate.arrivalTo
              ? todayDate <= periodDate.arrivalTo
              : false
          ),
        ),
      ).length,
      numPrices: Object.keys(season.prices).length,
      numAreas: Object.keys(season.seasonAreas).length,
      numDiscounts: Object.keys(season.discounts).length,
    }));
  },

  /**
   * @param state
   * @param getters
   * @returns {{numPeriodDates: *, numPrices: *, numAreas: *, arrivalTo: *, webActive: *, active: *,
   * twentySupplierId: *, arrivalFrom: *, netOrGross: *, priceSettings: *, numDiscounts: *,
   * numPersonCategories: *, name: *, id: *}}
   */
  getSeasonWithRelations(state, getters) {
    const season = state.season;
    const todayDate = getters.getTodayDate;
    let numPeriodDates = 0;
    if (season.periodDates.items.length) {
      numPeriodDates = Object.keys(
        season.periodDates.items.filter(
          periodDate => (
            periodDate.arrivalTo
              ? todayDate <= periodDate.arrivalTo
              : false
          ),
        ),
      ).length;
    }

    return {
      id: season.id,
      twentySupplierId: season.twentySupplierId,
      name: season.name,
      webActive: season.webActive,
      active: season.active,
      bookDaysBeforeArrival: season.bookDaysBeforeArrival,
      netOrGross: season.netOrGross,
      priceSettings: season.priceSettings,
      numPersonCategories: Object.keys(season.personCategories.items).length,
      numPeriodDates,
      numPrices: Object.keys(season.prices.items).length,
      numAreas: Object.keys(season.skiAreas.ids).length,
      numDiscounts: Object.keys(season.discounts.items).length,
    };
  },

  /**
   * @param state
   * @param getters
   * @returns {Array}
   */
  getPossibleCombinationsWithPrices(state, getters) {
    const statePricesWithDays = state.season.prices.items;
    const seasonId = state.season.id;
    const statePriceCombinations = getters.filterPossiblePriceCombinations(state);
    const onlyShowSavedPrices = state.season.priceCombinations.onlyShowSavedPrices;
    const stateSelectedNumberOfDays = state.season.priceCombinations.selectedNumberOfDays;
    const stateNewPriceItem = Object.assign({}, state.season.prices.newPriceItem);
    let mergedPrices = [];
    for (let k = 0; k < statePriceCombinations.length; k += 1) {
      let priceCombination = statePriceCombinations[k];
      const levelId = priceCombination.level.id;
      const skiTypeId = priceCombination.skiType.id;
      const skiPackageId = priceCombination.package.id;
      const personCategoryId = priceCombination.personCategory.id;
      let priceMatch = Object.assign({}, stateNewPriceItem);
      priceMatch.skiRentalSeasonId = seasonId;
      priceMatch.skiRentalLevelId = levelId;
      priceMatch.skiRentalSkiTypeId = skiTypeId;
      priceMatch.skiRentalPackageId = skiPackageId;
      priceMatch.skiRentalSeasonPersonCategoryId = personCategoryId;
      for (let i = 0; i < statePricesWithDays.length; i += 1) {
        const price = statePricesWithDays[i];
        if (levelId === price.skiRentalLevelId
          && skiTypeId === price.skiRentalSkiTypeId
          && skiPackageId === price.skiRentalPackageId
          && personCategoryId === price.skiRentalSeasonPersonCategoryId) {
          priceMatch = price;
          break;
        }
      }
      const priceDays = {};
      for (let n = 0; n < stateSelectedNumberOfDays.length; n += 1) {
        const dayNumber = stateSelectedNumberOfDays[n];
        let priceDayMatch;
        if (priceMatch.id) {
          const statePriceDays = Object.values(priceMatch.days);
          for (let p = 0; p < statePriceDays.length; p += 1) {
            const statePriceDay = statePriceDays[p];
            if (statePriceDay.days === dayNumber) {
              priceDayMatch = statePriceDay;
              break;
            }
          }
        }
        priceDays[n] = {
          id: priceDayMatch ? priceDayMatch.id : undefined,
          skiRentalSeasonPriceId: priceDayMatch ? priceDayMatch.skiRentalSeasonPriceId : undefined,
          days: stateSelectedNumberOfDays[n],
          price: priceDayMatch ? priceDayMatch.price : undefined,
        };
      }
      if (priceMatch.name === undefined) {
        const defaultCustomerText = priceCombination.skiType.name
          .concat(' ')
          .concat(priceCombination.package.name.toLowerCase());
        priceMatch = {
          ...priceMatch,
          name: defaultCustomerText,
        };
      }
      priceCombination = {
        ...priceCombination,
        price: priceMatch,
        days: priceDays,
      };
      mergedPrices[k] = priceCombination;
    }

    if (onlyShowSavedPrices) {
      mergedPrices = mergedPrices.filter(priceCombination => (
        priceCombination.price && priceCombination.price.id
      ));
    }

    return mergedPrices.sort((a, b) => a.personCategory.ageTo - b.personCategory.ageTo);
  },

  /**
   * @returns {Function}
   */
  filterPossiblePriceCombinations: () => (state) => {
    const statePriceCombinations = state.season.priceCombinations.items;
    if (!statePriceCombinations || !Object.keys(statePriceCombinations).length) {
      return [];
    }
    const selectedLevelIds = state.season.priceCombinations.selectedLevelIds;
    const selectedSkiTypeIds = state.season.priceCombinations.selectedSkiTypeIds;
    const selectedPackageIds = state.season.priceCombinations.selectedPackageIds;
    const selectedPersonCategoryIds = state.season.priceCombinations.selectedPersonCategoryIds;
    const onlyShowSavedPrices = state.season.priceCombinations.onlyShowSavedPrices;
    let priceCombinations = map(statePriceCombinations, priceCombination => ({
      level: priceCombination.level,
      package: priceCombination.package,
      skiType: priceCombination.skiType,
      personCategory: priceCombination.personCategory,
    }));
    if (!onlyShowSavedPrices) {
      priceCombinations = priceCombinations.filter(priceCombination => (
        selectedLevelIds.includes(priceCombination.level.id)
        && selectedPackageIds.includes(priceCombination.package.id)
        && selectedSkiTypeIds.includes(priceCombination.skiType.id)
        && selectedPersonCategoryIds.includes(priceCombination.personCategory.id)
      ));
    }
    return priceCombinations;
  },

  /**
   * @param state
   * @returns {Array|*[]}
   */
  getPossiblePriceCombinations(state) {
    const statePriceCombinations = state.season.priceCombinations.items;
    if (!statePriceCombinations || !Object.keys(statePriceCombinations).length) {
      return [];
    }
    const selectedLevelIds = state.season.priceCombinations.selectedLevelIds;
    const selectedSkiTypeIds = state.season.priceCombinations.selectedSkiTypeIds;
    const selectedPackageIds = state.season.priceCombinations.selectedPackageIds;
    const selectedPersonCategoryIds = state.season.priceCombinations.selectedPersonCategoryIds;
    const priceCombinations = map(statePriceCombinations, priceCombination => ({
      level: priceCombination.level,
      package: priceCombination.package,
      skiType: priceCombination.skiType,
      personCategory: priceCombination.personCategory,
    })).filter(priceCombination => (
      selectedLevelIds.includes(priceCombination.level.id)
      && selectedPackageIds.includes(priceCombination.package.id)
      && selectedSkiTypeIds.includes(priceCombination.skiType.id)
      && selectedPersonCategoryIds.includes(priceCombination.personCategory.id)
    ));

    return priceCombinations.sort((a, b) => a.personCategory.ageTo - b.personCategory.ageTo);
  },

  /**
   * @param state
   * @returns {Array}
   */
  getPersonCategories(state) {
    let personCategories = state.season.personCategories.items;
    if (!personCategories || !Object.keys(personCategories).length) {
      return [];
    }
    personCategories = map(state.season.personCategories.items, personCategory => ({
      id: personCategory.id,
      skiRentalSeasonId: personCategory.skiRentalSeasonId,
      name: personCategory.name,
      externalName: personCategory.externalName,
      ageFrom: personCategory.ageFrom,
      ageTo: personCategory.ageTo,
    }));

    return personCategories.sort((a, b) => a.ageFrom - b.ageFrom);
  },

  /**
   * @param state
   * @returns {this|Array}
   */
  getPersonCategoriesWithAllOption(state) {
    let personCategories = state.season.personCategories.items;
    if (!personCategories || !Object.keys(personCategories).length) {
      return [];
    }
    personCategories = map(state.season.personCategories.items, personCategory => ({
      id: personCategory.id,
      skiRentalSeasonId: personCategory.skiRentalSeasonId,
      nameWithAges: personCategory.name.concat(' (')
        .concat(personCategory.ageFrom)
        .concat('-')
        .concat(personCategory.ageTo)
        .concat(' years)'),
      name: personCategory.name,
      externalName: personCategory.externalName,
      ageFrom: personCategory.ageFrom,
      ageTo: personCategory.ageTo,
    }));

    return personCategories.sort((a, b) => a.ageFrom - b.ageFrom);
  },

  /**
   * @param state
   * @returns {this|Array}
   */
  getDiscounts(state) {
    let discounts = state.season.discounts.items;
    if (!discounts || !Object.keys(discounts).length) {
      return [];
    }

    discounts = map(state.season.discounts.items, discount => ({
      id: discount.id,
      skiRentalSeasonId: discount.skiRentalSeasonId,
      bookFrom: discount.bookFrom,
      bookTo: discount.bookTo,
      arrivalFrom: discount.arrivalFrom,
      arrivalTo: discount.arrivalTo,
      percentage: discount.percentage,
      personCategories: discount.personCategories,
    }));

    return discounts.sort((a, b) => a.bookFrom - b.bookFrom);
  },

  /**
   * @returns {string}
   */
  getTodayDate: () => new Date().toISOString().substr(0, 10),

  /**
   * @param state
   * @returns {this|Array}
   */
  getSkiSuppliers(state) {
    const stateSuppliers = state.suppliers.items;
    if (!stateSuppliers || !Object.keys(stateSuppliers).length) {
      return [];
    }
    const suppliers = map(stateSuppliers, supplier => ({
      id: supplier.id,
      name: supplier.name,
    }));

    return suppliers.sort((a, b) => a.name.localeCompare(b.name));
  },

  /**
   * @param state
   * @param getters
   * @returns {this|Array}
   */
  getPeriodDates(state, getters) {
    const statePeriodDates = state.season.periodDates.items;
    if (!statePeriodDates || !Object.keys(statePeriodDates).length) {
      return [];
    }
    const todayDate = getters.getTodayDate;
    const periodDates = map(statePeriodDates, periodDate => ({
      id: periodDate.id,
      skiRentalSeasonId: periodDate.skiRentalSeasonId,
      arrivalFrom: periodDate.arrivalFrom,
      arrivalTo: periodDate.arrivalTo,
      isActive: periodDate.arrivalTo
        ? todayDate <= periodDate.arrivalTo
        : false,
    }));

    return periodDates.sort((a, b) => {
      if (!b.arrivalTo) {
        return 0;
      }
      return b.arrivalTo.localeCompare(a.arrivalTo);
    });
  },

  /**
   * @param state
   * @returns {this|Array}
   */
  getSalesTypes(state) {
    const stateSalesTypes = state.salesTypes.items;
    if (!stateSalesTypes || !Object.keys(stateSalesTypes).length) {
      return [];
    }
    const salesTypes = map(stateSalesTypes, (salesTypeIdentifier) => {
      let salesTypeName = '';
      switch (salesTypeIdentifier) {
        case 'booking':
          salesTypeName = 'Pay now (default)';
          break;
        case 'bookingArrival':
          salesTypeName = 'Book & pay upon arrival';
          break;
        case 'arrival':
          salesTypeName = 'Pay upon arrival';
          break;
        default:
          salesTypeName = salesTypeIdentifier;
          break;
      }
      return {
        id: salesTypeIdentifier,
        name: salesTypeName,
      };
    });

    return salesTypes.sort((a, b) => a.name.localeCompare(b.name));
  },

  /**
   * @param state
   * @returns {this|Array}
   */
  getLevels(state) {
    const stateLevels = state.levels.items;
    if (!stateLevels || !Object.keys(stateLevels).length) {
      return [];
    }
    const levels = map(stateLevels, level => ({
      id: level.id,
      name: level.name,
      identifier: level.identifier,
      priority: level.priority,
    }));

    return levels.sort((a, b) => a.priority - b.priority);
  },

  /**
   * @param state
   * @returns {this|Array}
   */
  getPackages(state) {
    const statePackages = state.packages.items;
    if (!statePackages || !Object.keys(statePackages).length) {
      return [];
    }
    const skiPackages = map(statePackages, skiPackage => ({
      id: skiPackage.id,
      name: skiPackage.name,
      identifier: skiPackage.identifier,
      priority: skiPackage.priority,
    }));

    return skiPackages.sort((a, b) => a.priority - b.priority);
  },

  /**
   * @param state
   * @returns {this|Array}
   */
  getSkiTypes(state) {
    const stateSkiTypes = state.skiTypes.items;
    if (!stateSkiTypes || !Object.keys(stateSkiTypes).length) {
      return [];
    }
    const skiTypes = map(stateSkiTypes, skiType => ({
      id: skiType.id,
      name: skiType.name,
      identifier: skiType.identifier,
      priority: skiType.priority,
    }));

    return skiTypes.sort((a, b) => a.priority - b.priority);
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/ski/rental/getters.js